import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, InputAdornment, IconButton, Tooltip, useTheme } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { DATE_TIME_DRAWER_FORMAT } from '../../../util/constants';
import DateTimePickerInput from './DateTimePickerInput';
import { convertInputTimezone, convertToLocalTimezone } from '../../../util/util';
import moment from 'moment';
import { renderDigitalClockTimeView } from '@mui/x-date-pickers';

// Core date time picker component used across the app
function TimeZoneKeyboardDateTimePicker({
  error,
  touched,
  value,
  disabled,
  timezone,
  minDate,
  maxDate,
  onBlur,
  onOpen,
  onChange,
  label,
  type,
  dateType,
}) {
  const [mounted, setMounted] = useState(false);
  const timezoneSpecificDefaultDate = moment.tz(new Date(), timezone);
  const theme = useTheme();

  const getDateTimePickerTooltip = (value) => {
    if (value === 'detection_date') {
      return 'Ensure the date & time is set to the time the issue was detected.';
    }

    if (value === 'estimated_date') {
      return 'Please indicate an expected date and time for full service to be restored.';
    }

    if (value === 'resolved_date') {
      return 'Ensure the date & time is set to when all services were restored.';
    }
  };

  useEffect(() => {
    setMounted(true);
  }, [setMounted]);

  useEffect(() => {
    if (value && mounted) {
      onChange(undefined);
    }
  }, [timezone]);

  return (
    <Tooltip
      arrow
      placement="right"
      title={type === 'incident' ? getDateTimePickerTooltip(dateType) : ''}
    >
      <div style={{ marginBottom: 20, width: '100%' }}>
        <DateTimePickerInput
          error={error}
          touched={touched}
          label={label}
          format={DATE_TIME_DRAWER_FORMAT}
          value={value}
          disabled={disabled || !timezone}
          helperText=""
          onChange={(date) => onChange(convertInputTimezone(date, timezone))}
          onBlur={onBlur}
          onOpen={onOpen}
          minDateTime={minDate || undefined}
          maxDateTime={maxDate || undefined}
          views={['year', 'month', 'day', 'hours', 'minutes']}
          timeSteps={{ minutes: 15 }}
          clearable
          fullWidth
          removeErrorSpacing={value}
          initialFocusedDate={timezoneSpecificDefaultDate}
          slotProps={{
            desktopPaper: {
              elevation: 0,
              sx: {
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              },
            },
            textField: {
              error: !!error && !!touched,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton disabled={disabled || !timezone} size="small">
                      <DateRangeIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            },
          }}
        />
        {Boolean(value) && value.isValid() && (
          <Typography gutterBottom variant="caption" color="textSecondary">
            {type === 'maintenance_banner'
              ? `${convertToLocalTimezone(value).format('DD/MM/YYYY HH:mm z')}`
              : `Your local time: ${convertToLocalTimezone(value).format(DATE_TIME_DRAWER_FORMAT)}`}
          </Typography>
        )}
      </div>
    </Tooltip>
  );
}

TimeZoneKeyboardDateTimePicker.propTypes = {
  error: PropTypes.string,
  touched: PropTypes.bool,
  value: PropTypes.objectOf(PropTypes.any),
  type: PropTypes.string,
  dateType: PropTypes.string,
  onBlur: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  minDate: PropTypes.objectOf(PropTypes.any),
  maxDate: PropTypes.objectOf(PropTypes.any),
  timezone: PropTypes.string,
};

TimeZoneKeyboardDateTimePicker.defaultProps = {
  label: '',
  dateType: '',
  minDate: null,
  maxDate: null,
  value: null,
  timezone: '',
  error: '',
  touched: false,
};

export default TimeZoneKeyboardDateTimePicker;
